import BaseChart from './BaseChart'
import ItemsHelper from '../chart_helpers/ItemsHelper'
import RootHelper from '../chart_helpers/RootHelper'
import AxisHelper from '../chart_helpers/AxisHelper'
import { continuousExtent } from '../chart_helpers/AxisHelper'
import SelectableHelper from '../chart_helpers/SelectableHelper'
import ExportHelper from '../chart_helpers/ExportHelper'
import SizeHelper from '../chart_helpers/SizeHelper'

import { extent as d3_extent } from 'd3-array'
import { scaleLinear as d3_scaleLinear } from 'd3-scale'
import { wrapSvgText } from '../utils/textUtils'

const DEFAULT_SCATTER_R = 6
export default class ScatterPlotChart extends BaseChart {
  constructor(...args) {
    super(...args)
  }
  static defaultConfig = {
    groupAttr: {
      type: String,
      default: 'group',
    },
    xAttr: {
      type: String,
      default: 'x',
    },
    yAttr: {
      type: String,
      default: 'y',
    },
    sizeAttr: {
      type: [String, null],
      default: 'size',
    },
    mode: {
      type: [String],
      default: 'dots',
      options: ['dots', 'grid'],
    },
    /**
     * If set, all data points will have at least this radius.
     */
    minSize: {
      type: Number,
      default: 0,
      exposed: true,
    },
    /**
     * If set, all data points will have at most this radius.
     */
    maxSize: {
      type: Number,
      default: Infinity,
      exposed: true,
    },
    /**
     * Show/hide the label of each data point.
     */
    showLabels: {
      type: Boolean,
      default: true,
      exposed: true,
    },
    formatScatterLegend: {
      type: Function,
      exposed: true,
      default: function (d) {
        return d.id
      }
    },
    AxisHelper: {
      zoom: false,
      x: {
        scale: 'linear',
        getValue: function(d) {
          return d.x
        },
        getExtent: function(data) {
          let extent = continuousExtent(data, d => d[this.config.xAttr]) // d3_extent(data.map((d)=>d[this.config.xAttr]))
          // return padLinear(extent,0.2)
          return extent
        },
        grid: true,
      },
      y: {
        scale: 'linear',
        getValue: function(d) {
          return d.y
        },
        getExtent: function(data) {
          let extent = continuousExtent(data, d => d[this.config.yAttr]) //d3_extent(data.map((d)=>d[this.config.yAttr]))
          // return padLinear(extent,0.2)
          return extent
        },
        grid: true,
      },
    },
  }
  static helpers = [RootHelper, ItemsHelper, SelectableHelper, SizeHelper, AxisHelper, ExportHelper]
  checkConsistency() {
    return true
  }
  getLegendData() {
    let legendData = {}
    if (this.config.groupAttr) {
      let groups = this.data.reduce((res, d) => {
        res.push(d.group)
        return res
      }, [])
      groups = groups.filter((d, i) => groups.indexOf(d) === i)
      let groupData = groups.map(g => {
        return { id: g, group: g }
      })
      legendData.group = groupData
      legendData.group.icon = 'circle'
    }

    if (this.config.sizeAttr) {
      let sizes = this._sizeScale.nice().ticks(3)
      sizes = [sizes[0], sizes[sizes.length - 1]]
      sizes.splice(1, 0, sizes[0] + (sizes[1] - sizes[0]) / 2)

      let sizeData = sizes.map(d => ({ id: d, value: 2 * this._sizeScale(d) }))

      legendData.size = sizeData
      legendData.size.icon = 'circle'
      legendData.size.type = 'size'
    }
    return legendData
  }
  beforeHelpers() {
    this._sizeScale = d3_scaleLinear()
      .domain(d3_extent(this.data, d => d.size))
      .range([3, 15])
  }
  draw() {
    if (this.config.mode === 'grid') {
      this.itemsEnter
        .selectAll('.square')
        .data([null])
        .enter()
        .append('g')
        .attr('class', 'square')
        .append('rect')
      // this.itemsEnter.call(drawItem,'enter')
      this.itemsEnter
        .select('.square')
        .attr(
          'transform',
          d =>
            `translate(${this.axes.x.func.scale()(d[this.config.xAttr])},${this.axes.y.func.scale()(
              d[this.config.yAttr]
            )})`
        )
        .select('rect')
        .attr('fill', (d)=>{
          return this.heatmapColor(d)
        })
        .attr('width', () => this.axes.x.func.scale().bandwidth())
        .attr('height', () => this.axes.y.func.scale().bandwidth())
        .style('opacity', 0)

      this.itemsEnterTrans
        .select('.square')
        .select('rect')
        .style('opacity', d => Number(d.color !== null))
      this.itemsTrans
        .select('.square')
        .attr(
          'transform',
          d =>
            `translate(${this.axes.x.func.scale()(d[this.config.xAttr])},${this.axes.y.func.scale()(
              d[this.config.yAttr]
            )})`
        )
        .select('rect')
        .attr('fill', (d)=>{
          return this.heatmapColor(d)
        })
        .attr('width', () => this.axes.x.func.scale().bandwidth())
        .attr('height', () => this.axes.y.func.scale().bandwidth())
        .style('opacity', d => Number(d.color !== null))

      this.items.select('.square').select('rect').attr('fill', (d)=>{
        return this.heatmapColor(d)
      })
      this.itemsExitTrans.style('opacity', 0)
    } else {
      let safeR = d => {
        let max = this.config.maxSize ? this.config.maxSize : Infinity
        let min = this.config.minSize ? this.config.minSize : 0
        return this.config.sizeAttr
          ? Math.max(min, Math.min(max, this._sizeScale(d.size)))
          : DEFAULT_SCATTER_R
      }
      this.itemsEnter
        .selectAll('.point')
        .data([null])
        .enter()
        .append('g')
        .attr('class', 'point')
        .append('circle')
      this.itemsEnter
        .select('.point')
        .attr(
          'transform',
          d =>
            `translate(${this.axes.x.func.scale()(d[this.config.xAttr])},${this.axes.y.func.scale()(
              d[this.config.yAttr]
            )})`
        )
        .select('circle')
        .attr('fill', d => this.color(d))
        .attr('r', 0)
      this.itemsEnterTrans
        .select('.point')
        .select('circle')
        .attr('r', safeR)

      let tickFontSize = this.config.AxisHelper.fontSize || (this.config.isMobile ? 11 : 12)
      let mergedItems = this.items.merge(this.itemsEnter)
      let labels = mergedItems
        .select('.point')
        .selectAll('text')
        .data(this.config.showLabels ? [null] : [])
      labels
        .enter()
        .append('text')
        // .attr('text-anchor','middle')
        .attr('font-size', `${tickFontSize}px`)
        .attr('fill', 'currentColor')
      labels
        .exit()
        .transition()
        .style('opacity', 0)
        .remove()

      mergedItems
        .select('.point')
        .select('text')
        .text(d => this.config.formatScatterLegend(d))
        .call(wrapSvgText, 80)
        .attr('transform', d => `translate(${safeR(d) + 6},-${safeR(d) + 6})`)
        .selectAll('tspan')
        .attr('x', 0)
        .attr('y', 0)

      this.itemsEnter
        .select('.point')
        .select('text')
        .style('opacity', 0)
        .style('font-size', '0px')
      this.itemsEnterTrans
        .select('.point')
        .select('text')
        .style('opacity', d => (this.selection.map(d => d.id).includes(d.id) ? 1 : 0))
        .style('font-size', d => (this.selection.map(d => d.id).includes(d.id) ? 'inherit' : '0px'))
      this.itemsTrans
        .select('.point')
        .attr(
          'transform',
          d =>
            `translate(${this.axes.x.func.scale()(d[this.config.xAttr])},${this.axes.y.func.scale()(
              d[this.config.yAttr]
            )})`
        )
        .select('circle')
        .attr('fill', d => this.color(d))
        .attr('r', safeR)

      this.itemsExitTrans.style('opacity', 0)
    }
  }
  heatmapColor(d){
    let unselected = String(d.id).indexOf('--')>=0 && this.selection.length && this.selection.map(dd => dd.id).indexOf(String(d.id).replace(/--.*/, '')) < 0
    return unselected?this.rgb2grey(this.color(d)):this.color(d)
  }
  rgb2grey(rgb){
    let comps = rgb.replace('rgb(','').replace(')','').split(',').map((c)=>+c)
    let grey = (comps[0]+comps[1]+comps[2]) / 3
    // return `rgb(${grey},${0},${0})`
    return `rgb(${grey},${grey},${grey})`
  }
  // setSelectedFunc(sel) {
  //   // console.log("should set hightliht",d,this)
  //   sel.select('.square').select('rect').attr('fill',(d)=>this.color(d))
  // }
  // unsetSelectedFunc(sel) {
  //   // console.log("should unset hightliht",d,this)
  //   if(this.selection.length) sel.select('.square').select('rect').attr('fill',(d)=>this.rgb2grey(this.color(d)))
  //   else this.setSelectedFunc(sel)
  // }
}
